// src/hooks/enablement-journey-map-hooks.ts
//
// Journey-map completion for the Leader "Enablement" tab, HYBRID sourced:
//   • `useAllJourneyMapAssignments` — one bulk pull of every assignment row, scoped client-side by the
//     org tree (mirrors `useFetchAllEnrollments`). Feeds the manager/rollup counts, which only need
//     per-user done/assigned totals, never per-item detail.
//   • `useUserJourneyMapDetail` — the per-item drill for a SINGLE drilled IC, generalizing the fetch
//     sequence in `campaign-journey-map.ts` (assignments → items → item-progress) to an arbitrary user.
//
// Neither hook writes anything; both are read-only rollups over the generic `db/journey-map-*` CRUD.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems } from '../support/queries';
import { DEFAULT_QUERY_OPTIONS } from '../support/constants';

/** An active-or-inactive journey-map assignment row as `db/journey-map-assignment/` serializes it. */
export interface JourneyMapAssignmentRow {
  id: number;
  journey_map: number;
  user: number;
  progress_percent?: number | null;
  completed_date?: string | null;
  assigned_date?: string | null;
  due_date?: string | null;
  start_date?: string | null;
  is_required?: boolean | null;
  is_active?: boolean;
  journey_map_name?: string | null;
  required_percent?: number | null;
  completion_threshold_pct?: number | null;
  journey_map_required_percent?: number | null;
  [k: string]: any;
}

const asArray = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const isRowCompleted = (row: any): boolean =>
  String(row?.path_state || '').toLowerCase() === 'completed' ||
  String(row?.status_name || '').toLowerCase() === 'completed' ||
  !!row?.completed_date;

/**
 * Every journey-map assignment, unfiltered. Small table; fine to pull whole (one page of up to 1000).
 * Callers scope by `user` id over the org subtree client-side, exactly like `useFetchAllEnrollments`.
 * NOTE: if assignments ever exceed a page, this needs the cursor-pagination follow-up the delivery/
 * enrollment "fetch all" idiom uses — today one page covers the whole org.
 */
export function useAllJourneyMapAssignments(options?: object) {
  return useQuery<JourneyMapAssignmentRow[]>({
    queryKey: ['all-journey-map-assignments'],
    queryFn: async () =>
      asArray(
        await fetchItems('db/journey-map-assignment/', { size: 1000 }, { includePageSize: false }),
      ),
    ...{ ...DEFAULT_QUERY_OPTIONS, ...(options || {}) },
  });
}

/** One assigned journey map for a drilled IC, with per-item completion. */
export interface UserJourneyMap {
  assignmentId: number;
  mapId: number;
  mapName: string;
  percent: number | null;
  completedDate: string | null;
  /** Assignment due date, if one was set. */
  dueDate: string | null;
  /** When the map was assigned to this person. */
  assignedDate: string | null;
  /** When the person started it, if they have. */
  startDate: string | null;
  /** Whether the assignment was made Required. Absent on servers that predate the flag. */
  isRequired?: boolean | null;
  requiredPercent: number | null;
  done: boolean;
  items: any[];
  totalItems: number;
  completedItems: number;
}

export interface UserJourneyMapDetail {
  maps: UserJourneyMap[];
  assignedCount: number;
  completedCount: number;
  isLoading: boolean;
}

/** Whether an assignment counts as complete: a completion date, or percent past the required threshold. */
export function assignmentRowDone(a: JourneyMapAssignmentRow): boolean {
  if (a?.completed_date) return true;
  const required =
    a?.required_percent ??
    a?.completion_threshold_pct ??
    a?.journey_map_required_percent ??
    100;
  const pct = a?.progress_percent != null ? Number(a.progress_percent) : null;
  return pct != null && Number.isFinite(pct) && pct >= Number(required);
}

/**
 * Per-map, per-item journey progress for ONE user — the IC detail view. Iterates all the user's active
 * assignments, loading each map's items and the user's item-progress, and derives completion the same
 * way the Enablement JourneyMapDetailPage does. Read-only.
 */
export function useUserJourneyMapDetail(userId?: number | null): UserJourneyMapDetail {
  const { data: assignmentsRaw, isLoading: loadingAssignments } = useQuery({
    queryKey: ['user-journey-map-assignments', userId],
    queryFn: async () =>
      asArray(await fetchItems('db/journey-map-assignment/', { user: userId }, { includePageSize: false })),
    ...{ enabled: userId != null, ...DEFAULT_QUERY_OPTIONS },
  });

  const assignments = useMemo<JourneyMapAssignmentRow[]>(
    () => asArray(assignmentsRaw).filter((a) => a?.is_active !== false),
    [assignmentsRaw],
  );

  const mapIds = useMemo(
    () => assignments.map((a) => Number(a?.journey_map)).filter((n) => Number.isFinite(n)),
    [assignments],
  );
  const mapKey = mapIds.join(',');

  const assignmentIds = useMemo(
    () => assignments.map((a) => a?.id).filter((id) => id != null),
    [assignments],
  );
  const assignmentKey = assignmentIds.join(',');

  // Items for every assigned map, keyed by map id (item ids are globally unique, so one flat progress
  // map is safe). `size: 200` reaches the URL even with includePageSize:false.
  const { data: itemsByMap, isLoading: loadingItems } = useQuery({
    queryKey: ['user-journey-map-items', mapKey],
    queryFn: async () => {
      const perMap = await Promise.all(
        mapIds.map((id) =>
          fetchItems('db/journey-map-item/', { journey_map: id, size: 200 }, { includePageSize: false }),
        ),
      );
      const out: Record<number, any[]> = {};
      mapIds.forEach((id, i) => {
        out[id] = asArray(perMap[i]);
      });
      return out;
    },
    ...{ enabled: mapIds.length > 0, ...DEFAULT_QUERY_OPTIONS },
  });

  // Item-progress unioned across every assignment, keyed by item id.
  const { data: completionByItemId, isLoading: loadingProgress } = useQuery({
    queryKey: ['user-journey-map-item-progress', assignmentKey, userId],
    queryFn: async () => {
      const perAssignment = await Promise.all(
        assignmentIds.map((aid) =>
          fetchItems(
            'db/journey-map-item-progress/',
            { assignment: aid, user: userId },
            { includePageSize: false },
          ),
        ),
      );
      const completion: Record<string, boolean> = {};
      perAssignment.flatMap((r) => asArray(r)).forEach((row) => {
        const itemId = String(row?.journey_map_item);
        if (itemId) completion[itemId] = isRowCompleted(row);
      });
      return completion;
    },
    ...{ enabled: userId != null && assignmentIds.length > 0, ...DEFAULT_QUERY_OPTIONS },
  });

  const maps = useMemo<UserJourneyMap[]>(() => {
    const itemMap = itemsByMap || {};
    const completion = completionByItemId || {};
    return assignments
      .map((a) => {
        const mapId = Number(a?.journey_map);
        const items = itemMap[mapId] || [];
        const completedItems = items.reduce(
          (n, it) => n + (completion[String(it?.id)] ? 1 : 0),
          0,
        );
        const pct = a?.progress_percent != null ? Number(a.progress_percent) : null;
        const requiredRaw =
          a?.required_percent ?? a?.completion_threshold_pct ?? a?.journey_map_required_percent ?? null;
        return {
          assignmentId: Number(a?.id),
          mapId,
          mapName: a?.journey_map_name || `Journey Map ${mapId}`,
          percent: pct != null && Number.isFinite(pct) ? pct : null,
          completedDate: (a?.completed_date as string | null) ?? null,
          dueDate: (a?.due_date as string | null) ?? null,
          assignedDate: (a?.assigned_date as string | null) ?? null,
          startDate: (a?.start_date as string | null) ?? null,
          isRequired: a?.is_required ?? undefined,
          requiredPercent:
            requiredRaw != null && Number.isFinite(Number(requiredRaw)) ? Number(requiredRaw) : null,
          done: assignmentRowDone(a),
          items,
          totalItems: items.length,
          completedItems,
        } as UserJourneyMap;
      })
      .sort((a, b) => a.mapName.localeCompare(b.mapName));
  }, [assignments, itemsByMap, completionByItemId]);

  return {
    maps,
    assignedCount: maps.length,
    completedCount: maps.filter((m) => m.done).length,
    isLoading: loadingAssignments || loadingItems || loadingProgress,
  };
}
